import { reviewDocumentQueue, DocumentReviewNavigation } from './document-review-queue';
import { MatDialog } from '@angular/material/dialog';
import { firstValueFrom } from 'rxjs';
import { PDFDocument } from 'pdf-lib';
import type { Document } from '../model';
import type { ManagedDocument } from '../common/models/document.model';
import { DocumentService } from '../common/service/document.service';
import { NotificationService } from '../common/service/notification.service';
import { DocumentUploadService } from './document-upload.service';
import { ZipExplorerDialogComponent, ZipExplorerResult } from './zip-explorer-dialog.component';
import type { WorkFileAnalysisTask, WorkbenchResult, DocumentWorkbenchDialogData } from './document-workbench.model';
import type { DocumentCategory, UploadQueuePosition } from './document-link.model';

export interface DocumentLinkUploadContext {
  enforcedCategory: () => DocumentCategory | null;
  reloadDocuments: () => Promise<void>;
  selectUploadedDocument: (id: string) => void;
}

/** File batching, analysis, workbench review, ZIP extraction and final upload. */
export class DocumentLinkUploadFlow {
  constructor(
    private readonly dialog: MatDialog,
    private readonly documentService: DocumentService,
    private readonly notification: NotificationService,
    private readonly uploadService: DocumentUploadService,
    private readonly context: DocumentLinkUploadContext
  ) {}
  async handleUploadFiles(files: File[]) {
    const supportedFiles = files.filter((file) => this.isSupportedUploadFile(file));
    const unsupportedFiles = files.filter((file) => !this.isSupportedUploadFile(file));
    for (const file of unsupportedFiles) {
      console.warn('Unsupported file type', file.name);
    }
    // Classify the batch up front so later files continue processing while the
    // user reviews the current file in the workbench.
    const analyses = new Map<File, WorkFileAnalysisTask>();
    for (const file of supportedFiles) {
      if ((file.name.split('.').pop()?.toLowerCase() ?? '') !== 'zip') {
        analyses.set(file, this.prepareUploadAnalysis(file));
      }
    }
    const { uploaded, skipped } = await reviewDocumentQueue(supportedFiles, async (file, queue) => {
      if (file.name.toLowerCase().endsWith('.zip')) return this.handleZipUpload(file, queue);
      return this.uploadAndReviewDocument(file, queue, analyses.get(file));
    });
    if (supportedFiles.length > 1) {
      await this.notification.success('Multi_file_upload_summary_tr', { uploaded, skipped });
    }
  }

  isSupportedUploadFile(file: File) {
    const extension = file.name.split('.').pop()?.toLowerCase() ?? '';
    return extension === 'pdf' || extension === 'zip' || ['png', 'jpg', 'jpeg', 'gif', 'webp'].includes(extension);
  }

  lookupMimeType(extension: string) {
    if (extension === 'pdf') {
      return 'application/pdf';
    }
    if (extension === 'jpg') {
      return 'image/jpeg';
    }
    if (['png', 'jpeg', 'gif', 'webp'].includes(extension)) {
      return `image/${extension}`;
    }
    return 'application/octet-stream';
  }

  async countPdfPages(blob: Blob) {
    try {
      const pdf = await PDFDocument.load(await blob.arrayBuffer());
      return pdf.getPageCount();
    } catch {
      return 1;
    }
  }

  async uploadAndReviewDocument(
    file: File,
    queue?: UploadQueuePosition,
    analysisTask?: WorkFileAnalysisTask
  ): Promise<boolean> {
    const extension = file.name.split('.').pop()?.toLowerCase() ?? '';
    const normalizedExtension = extension || (file.type === 'application/pdf' ? 'pdf' : 'png');
    const title = file.name.replace(/\.[^/.]+$/, '') || 'Document';
    const mimeType = file.type || this.lookupMimeType(normalizedExtension);
    const blob = file.slice(0, file.size, mimeType);
    const tempDocument: ManagedDocument = {
      id: crypto.randomUUID ? crypto.randomUUID() : Math.random().toString(36).slice(2),
      title,
      mimeType,
      extension: normalizedExtension,
      kind: normalizedExtension === 'pdf' ? 'pdf' : 'image',
      blob,
      size: file.size,
      createdAt: new Date().toISOString(),
      pages: normalizedExtension === 'pdf' ? await this.countPdfPages(blob) : 1,
      source: 'upload',
    };
    const workFileTask = analysisTask ?? this.prepareUploadAnalysis(file, tempDocument.pages);
    const { DocumentWorkbenchDialogComponent } = await import('./document-workbench-dialog.component');
    const dialogData =
      normalizedExtension === 'pdf'
        ? ({
            mode: 'split',
            document: tempDocument,
            pdfDataUrl: await this.toDataUrl(blob, mimeType),
            maxPages: tempDocument.pages,
            documentTypeCategory: this.context.enforcedCategory() ?? null,
            allowSaveUpload: true,
            actionLabels: 'link',
            workFile: workFileTask.workFile,
            workFileAnalysis: workFileTask.analysis,
            queuePosition: queue?.position,
            queueTotal: queue?.total,
            reviewNavigation: queue?.navigation,
          } as const)
        : ({
            mode: 'preview',
            document: tempDocument,
            documentTypeCategory: this.context.enforcedCategory() ?? null,
            allowSaveUpload: true,
            actionLabels: 'link',
            workFile: workFileTask.workFile,
            workFileAnalysis: workFileTask.analysis,
            queuePosition: queue?.position,
            queueTotal: queue?.total,
            reviewNavigation: queue?.navigation,
          } as const);
    const result = await firstValueFrom(
      this.dialog
        .open<
          InstanceType<typeof DocumentWorkbenchDialogComponent>,
          DocumentWorkbenchDialogData,
          WorkbenchResult | undefined
        >(DocumentWorkbenchDialogComponent, {
          data: dialogData,
          height: '100dvh',
          maxHeight: '100dvh',
          width: '100vw',
          maxWidth: '100vw',
          panelClass: 'document-workbench-dialog',
        })
        .afterClosed()
    );
    if (!result) {
      return false;
    }

    if (result.kind === 'split' || result.kind === 'split-groups' || result.action === 'save-upload') {
      await this.uploadService.ensureWorkFile(tempDocument, workFileTask.workFile);
    }
    if (result.kind === 'split') {
      return await this.uploadSplitPdfResult(tempDocument, result);
    }
    if (result.kind === 'split-groups') {
      let uploadedAny = false;
      let failedCount = 0;
      const remainingGroups: typeof result.groups = [];
      for (const group of result.groups) {
        try {
          const uploaded = await this.uploadSplitPdfResult(tempDocument, {
            title: group.title,
            pages: group.pages,
            metadata: group.metadata,
            action: result.action,
          });
          uploadedAny = uploaded || uploadedAny;
          if (!uploaded) remainingGroups.push(group);
        } catch (error) {
          failedCount++;
          remainingGroups.push(group);
          console.error('Failed to upload a split document group', group.title, error);
        }
      }
      if (failedCount > 0) {
        await this.notification.error('Some_document_groups_failed_tr', { count: failedCount });
      }
      if (remainingGroups.length && queue?.navigation?.draft) {
        const draft = { ...queue.navigation.draft, groups: remainingGroups };
        queue.navigation.navigate('skip', draft);
        return false;
      }
      return uploadedAny;
    }

    if (result.kind !== 'metadata' || result.action !== 'save-upload') {
      return false;
    }

    try {
      const uploaded = await this.uploadService.uploadReviewedDocument(tempDocument, result.metadata);
      if (!uploaded) {
        return false;
      }
      const formattedDate = result.metadata.documentDate ? result.metadata.documentDate.toISOString() : undefined;
      if (formattedDate) {
        await this.documentService.update(uploaded.id, { ...uploaded, date: formattedDate } as Document);
      }
      await this.context.reloadDocuments();
      this.context.selectUploadedDocument(uploaded.id);
      return true;
    } catch (error) {
      console.error('Failed to upload document from linking dialog', error);
      throw error;
    }
  }

  prepareUploadAnalysis(file: File, knownPageCount = 1): WorkFileAnalysisTask {
    const workFile = this.documentService.uploadWorkFile(file);
    const analysis = workFile
      .then(async (uploaded) => {
        try {
          const pageCount = uploaded.pageCount ?? knownPageCount;
          const pages = Array.from({ length: Math.max(pageCount, 1) }, (_, index) => index + 1);
          const response = await this.documentService.analyzeWorkFileWhenReady(uploaded.fileHash, pages);
          return { suggestion: response.analysis, failed: false, workFileHash: uploaded.fileHash };
        } catch (error) {
          console.warn('Failed to analyze uploaded document type', error);
          return { suggestion: undefined, failed: true, workFileHash: uploaded.fileHash };
        }
      })
      .catch((error) => {
        console.warn('Failed to upload work file for analysis', error);
        return { suggestion: undefined, failed: true };
      });
    return { workFile, analysis };
  }

  async uploadSplitPdfResult(
    sourceDocument: ManagedDocument,
    result: {
      title: string;
      pages: number[];
      metadata: { documentTypeId?: string; documentDate?: Date; title: string };
      action?: string;
    }
  ): Promise<boolean> {
    const title = result.title?.trim() || sourceDocument.title;
    const uploaded = await this.uploadService.uploadReviewedDocument(
      sourceDocument,
      { ...result.metadata, title },
      result.pages
    );
    if (!uploaded) {
      return false;
    }
    const formattedDate = result.metadata.documentDate ? result.metadata.documentDate.toISOString() : undefined;
    if (formattedDate) {
      await this.documentService.update(uploaded.id, { ...uploaded, date: formattedDate } as Document);
    }
    await this.context.reloadDocuments();
    this.context.selectUploadedDocument(uploaded.id);
    return true;
  }

  async handleZipUpload(file: File, queue?: UploadQueuePosition): Promise<boolean> {
    const dialogRef = this.dialog.open(ZipExplorerDialogComponent, {
      data: {
        file,
        mode: 'link',
        documentTypeCategory: this.context.enforcedCategory() ?? null,
        queuePosition: queue?.position,
        queueTotal: queue?.total,
      },
      width: '100vw',
      maxWidth: '100vw',
      height: '100dvh',
      maxHeight: '100dvh',
      panelClass: 'zip-explorer-dialog',
    });
    const results = await firstValueFrom(dialogRef.afterClosed());
    if (!results?.length) {
      return false;
    }
    for (const result of results) {
      await this.uploadZipEntry(result);
    }
    await this.context.reloadDocuments();
    return true;
  }

  async uploadZipEntry(result: ZipExplorerResult) {
    if (!result.documentTypeId) {
      return;
    }
    const blob = new Blob([result.data.slice()], { type: result.mimeType });
    const title = result.title?.trim() || 'Document';
    const tempDocument: ManagedDocument = {
      id: crypto.randomUUID ? crypto.randomUUID() : Math.random().toString(36).slice(2),
      title,
      mimeType: result.mimeType,
      extension: result.extension,
      kind: result.extension === 'pdf' ? 'pdf' : 'image',
      blob,
      size: result.size,
      createdAt: new Date().toISOString(),
      pages: result.extension === 'pdf' ? await this.countPdfPages(blob) : 1,
      source: 'zip-entry',
      documentTypeId: result.documentTypeId ?? undefined,
      documentTypeName: result.documentTypeName ?? undefined,
      documentDate: result.documentDate ? result.documentDate.toISOString() : new Date().toISOString(),
    };
    const uploaded = await this.uploadService.uploadReviewedDocument(tempDocument, {
      title,
      documentTypeId: result.documentTypeId,
      documentDate: result.documentDate ?? undefined,
    });
    if (!uploaded) {
      return;
    }
    const formattedDate = tempDocument.documentDate ?? undefined;
    if (formattedDate) {
      await this.documentService.update(uploaded.id, { ...uploaded, date: formattedDate } as Document);
    }
    await this.context.reloadDocuments();
    this.context.selectUploadedDocument(uploaded.id);
  }

  async toDataUrl(blob: Blob, mimeType: string) {
    return await new Promise<string>((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve(reader.result as string);
      reader.onerror = () => reject(reader.error);
      reader.readAsDataURL(new Blob([blob], { type: mimeType }));
    });
  }
}
